import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Circle, GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Crosshair, MapPin, RefreshCw, Search, Truck } from "lucide-react";
import AutocompleteInput from "../../LocationsModule/PlaceAutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useGoogleMaps } from "../../deliveryModule/components/DeliveryMap/useGoogleMaps";
import {
  AdminAccessProvider,
  useAdminAccess,
} from "../Analytics/shared/AdminAccess/AdminAccessContext";
import {
  createVehicleMarkerIcon,
  formatLocationAge,
  reverseGeocodeLatLng,
  vehicleLabel,
} from "./driverMapUtils";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const DEFAULT_CENTER = { lat: -13.9626, lng: 33.7741 };
const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

const clean = (value: unknown) => String(value || "").trim();

const toArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const isClaimable = (status: unknown) =>
  ["pending_dispatch", "notified", "claimed"].includes(
    clean(status).toLowerCase(),
  );

const getJobLabel = (job: any) => {
  const id = clean(job?._id);
  const from = clean(job?.pickupAddress) || "Pickup";
  const to = clean(job?.dropoffAddress) || "Drop-off";
  return `${id.slice(-8) || "Job"} • ${from} → ${to}`;
};

const DriverLiveMapContent: React.FC = () => {
  const { theme } = useCustomTheme();
  const toast = useToast();
  const { user } = useAuth();
  const { userId, isSuperAdmin, loading: accessLoading } = useAdminAccess();
  const { isLoaded, loadError } = useGoogleMaps();
  const routeLocation = useLocation();

  const mapRef = useRef<google.maps.Map | null>(null);
  const fitMapRequestedRef = useRef(true);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [centerAddress, setCenterAddress] = useState("Lilongwe, Malawi");
  const [radiusKm, setRadiusKm] = useState(25);
  const [serviceCategory, setServiceCategory] = useState("delivery");
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [includeStale, setIncludeStale] = useState(false);
  const [driverSearch, setDriverSearch] = useState("");

  const [drivers, setDrivers] = useState<any[]>([]);
  const [warehouseDriverCount, setWarehouseDriverCount] = useState(0);
  const [nearbyDriverCount, setNearbyDriverCount] = useState(0);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState("");
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<any | null>(null);
  const [selectedDriverAddress, setSelectedDriverAddress] = useState("");

  const [jobs, setJobs] = useState<any[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [assigningDriverId, setAssigningDriverId] = useState("");

  const scopedJobs = useMemo(() => {
    if (isSuperAdmin) return jobs;
    if (!userId) return [];

    return jobs.filter((job) => {
      const ids = [
        job?.ownerUserId,
        job?.requesterUserId,
        job?.metadata?.requesterUserId,
        job?.metadata?.ownerUserId,
      ]
        .map(clean)
        .filter(Boolean);
      return ids.includes(userId);
    });
  }, [isSuperAdmin, jobs, userId]);

  const getAuthenticatedConfig = useCallback(async () => {
    const token = await user?.getIdToken();
    if (!token) {
      throw new Error("Authentication required for driver operations.");
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [user]);

  const loadJobs = useCallback(
    async (silent = false) => {
      if (accessLoading || !userId) return;

      try {
        if (!silent) setJobsLoading(true);
        const config = await getAuthenticatedConfig();
        const response = await axios.get(
          `${API_BASE}/api/v1/dispatch/jobs`,
          config,
        );
        setJobs(toArray(response.data));
      } catch (error) {
        console.error("Failed to load dispatch jobs for driver map", error);
        if (!silent) setJobs([]);
      } finally {
        if (!silent) setJobsLoading(false);
      }
    },
    [accessLoading, getAuthenticatedConfig, userId],
  );

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const loadNearbyDrivers = useCallback(
    async (options?: {
      point?: { lat: number; lng: number };
      radius?: number;
      job?: any | null;
      silent?: boolean;
    }) => {
      const point = options?.point || center;
      const radius = Number(options?.radius || radiusKm);
      const job = options?.job === undefined ? selectedJob : options.job;

      try {
        if (!options?.silent) setDriversLoading(true);
        setDriversError("");

        const jobOwnerId = clean(job?.ownerUserId);
        const ownerUserId = jobOwnerId || (!isSuperAdmin ? clean(userId) : "");
        const jobService = clean(job?.metadata?.orderType);
        const requestedService = jobService || serviceCategory;
        const jobDispatchScope = clean(
          job?.dispatchScope || job?.metadata?.dispatchScope,
        );
        const warehouseId =
          job && jobDispatchScope !== "platform_direct"
            ? clean(job?.warehouseId)
            : "";

        const config = await getAuthenticatedConfig();
        const response = await axios.get(`${API_BASE}/api/v1/drivers/nearby`, {
          ...config,
          params: {
            lat: point.lat,
            lng: point.lng,
            radiusKm: radius,
            maxAgeMinutes: 15,
            onlyAvailable,
            includeStale,
            serviceCategory: requestedService,
            ownerUserId: ownerUserId || undefined,
            warehouseId: warehouseId || undefined,
            dispatchJobId: clean(job?._id) || undefined,
            search: driverSearch || undefined,
            limit: 200,
          },
        });

        const result = response.data?.data || {};
        const rows = Array.isArray(result?.drivers) ? result.drivers : [];
        setDrivers(rows);
        setSelectedDriver((current: any) => {
          if (!current?.id) return current;
          return (
            rows.find((row: any) => clean(row?.id) === clean(current.id)) ||
            current
          );
        });
        setWarehouseDriverCount(Number(result?.warehouseDriverCount || 0));
        setNearbyDriverCount(Number(result?.nearbyDriverCount || 0));
        setLastUpdatedAt(new Date());

        if (!options?.silent && !rows.length) {
          toast({
            title: "No drivers found in this radius",
            description:
              "Try a larger radius, include stale locations, or check that drivers are on shift and sharing location.",
            status: "info",
            duration: 3500,
            isClosable: true,
          });
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Could not load nearby drivers.";

        if (!options?.silent) {
          setDrivers([]);
          setWarehouseDriverCount(0);
          setNearbyDriverCount(0);
          setDriversError(message);
          toast({
            title: "Failed to load nearby drivers",
            description: message,
            status: "error",
            duration: 4500,
            isClosable: true,
          });
        } else {
          console.warn(
            "Silent live driver refresh failed; keeping last known rows",
            error,
          );
        }
      } finally {
        if (!options?.silent) setDriversLoading(false);
      }
    },
    [
      center,
      driverSearch,
      includeStale,
      isSuperAdmin,
      onlyAvailable,
      radiusKm,
      selectedJob,
      serviceCategory,
      toast,
      getAuthenticatedConfig,
      userId,
    ],
  );

  const refreshCurrentMapData = useCallback(
    async (options?: { silent?: boolean; radius?: number }) => {
      const radius = Number(options?.radius || radiusKm);
      let jobForDriverSearch = selectedJob;

      if (
        selectedJob?._id &&
        !selectedJob.assignedDriverId &&
        isClaimable(selectedJob.status)
      ) {
        try {
          const config = await getAuthenticatedConfig();
          const response = await axios.post(
            `${API_BASE}/api/v1/dispatch/jobs/${selectedJob._id}/refresh-candidates`,
            { radiusKm: radius, expandRadius: false },
            config,
          );
          const refreshedJob = response.data?.data || selectedJob;
          jobForDriverSearch = refreshedJob;
          setSelectedJob(refreshedJob);
          setJobs((current) =>
            current.map((row) =>
              clean(row?._id) === clean(refreshedJob?._id)
                ? { ...row, ...refreshedJob }
                : row,
            ),
          );
        } catch (error) {
          console.warn(
            "Live candidate refresh failed; keeping current candidates",
            error,
          );
        }
      }

      await loadNearbyDrivers({
        radius,
        job: jobForDriverSearch,
        silent: options?.silent,
      });
    },
    [getAuthenticatedConfig, loadNearbyDrivers, radiusKm, selectedJob],
  );

  useEffect(() => {
    if (!isLoaded || accessLoading || !userId || !autoRefreshEnabled) return;

    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      void refreshCurrentMapData({ silent: true });
    };

    refreshIfVisible();
    const interval = window.setInterval(refreshIfVisible, 10000);
    document.addEventListener("visibilitychange", refreshIfVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [
    accessLoading,
    autoRefreshEnabled,
    isLoaded,
    refreshCurrentMapData,
    userId,
  ]);

  useEffect(() => {
    if (accessLoading || !userId || !autoRefreshEnabled) return;

    const refreshJobsIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      void loadJobs(true);
    };

    const interval = window.setInterval(refreshJobsIfVisible, 30000);
    return () => window.clearInterval(interval);
  }, [accessLoading, autoRefreshEnabled, loadJobs, userId]);

  useEffect(() => {
    if (!isLoaded) return;

    void reverseGeocodeLatLng(center).then((address) => {
      if (address) setCenterAddress(address);
    });
  }, [center, isLoaded]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Location unavailable",
        description: "This browser does not support geolocation.",
        status: "warning",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const point = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setSelectedJobId("");
        setSelectedJob(null);
        fitMapRequestedRef.current = true;
        setCenter(point);
        mapRef.current?.panTo(point);
        mapRef.current?.setZoom(13);
        void loadNearbyDrivers({ point, job: null });
      },
      (error) => {
        toast({
          title: "Could not read your location",
          description: error.message,
          status: "error",
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const handlePlaceSelected = (address: string) => {
    setCenterAddress(address);
    setSelectedJobId("");
    setSelectedJob(null);
  };

  const handleLocationSelected = (point: { lat: number; lng: number }) => {
    fitMapRequestedRef.current = true;
    setCenter(point);
    mapRef.current?.panTo(point);
    mapRef.current?.setZoom(13);
    void loadNearbyDrivers({ point, job: null });
  };

  const selectJob = async (jobId: string) => {
    setSelectedJobId(jobId);
    const job =
      scopedJobs.find((row) => clean(row?._id) === clean(jobId)) || null;
    setSelectedJob(job);
    setSelectedDriver(null);
    setSelectedDriverAddress("");

    if (!job) return;

    const pickup = job?.pickupLatLng;
    const lat = Number(pickup?.lat);
    const lng = Number(pickup?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      toast({
        title: "Pickup coordinates missing",
        description:
          "This older dispatch job has no pickup coordinates. Keep the existing assignment flow or update the source location first.",
        status: "warning",
        duration: 4500,
        isClosable: true,
      });
      return;
    }

    const rawJobRadius = Number(
      job?.metadata?.searchRadiusKm || radiusKm || 25,
    );
    const jobRadius =
      Number.isFinite(rawJobRadius) && rawJobRadius > 0
        ? Math.min(250, rawJobRadius)
        : 25;
    const point = { lat, lng };
    fitMapRequestedRef.current = true;
    setRadiusKm(jobRadius);
    setServiceCategory(clean(job?.metadata?.orderType) || "delivery");
    setCenter(point);
    setCenterAddress(clean(job?.pickupAddress) || "Pickup location");
    mapRef.current?.panTo(point);
    mapRef.current?.setZoom(13);

    try {
      const config = await getAuthenticatedConfig();
      const refresh = await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${job._id}/refresh-candidates`,
        { radiusKm: jobRadius, expandRadius: false },
        config,
      );
      const refreshed = refresh.data?.data || job;
      setSelectedJob(refreshed);
      setJobs((current) =>
        current.map((row) =>
          clean(row?._id) === clean(job._id) ? { ...row, ...refreshed } : row,
        ),
      );
      await loadNearbyDrivers({ point, radius: jobRadius, job: refreshed });
    } catch (error) {
      console.error("Failed to refresh selected job candidates", error);
      await loadNearbyDrivers({ point, radius: jobRadius, job });
    }
  };

  useEffect(() => {
    if (!scopedJobs.length || selectedJobId) return;

    const params = new URLSearchParams(routeLocation.search);
    const requestedJobId = clean(params.get("jobId"));
    if (!requestedJobId) return;

    if (scopedJobs.some((job) => clean(job?._id) === requestedJobId)) {
      void selectJob(requestedJobId);
    }
    // selectJob intentionally runs once when a routed job becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation.search, scopedJobs, selectedJobId]);

  const selectedJobCandidateIds = useMemo(
    () =>
      new Set(
        Array.isArray(selectedJob?.candidateDriverIds)
          ? selectedJob.candidateDriverIds.map((value: unknown) => clean(value))
          : [],
      ),
    [selectedJob],
  );

  const assignDriver = async (driver: any) => {
    if (!selectedJob?._id) {
      toast({
        title: "Select a dispatch job first",
        description: "Choose the order/delivery whose pickup should be served.",
        status: "warning",
      });
      return;
    }

    if (!isClaimable(selectedJob.status) || selectedJob.assignedDriverId) {
      toast({
        title: "Job is not assignable",
        description: "This dispatch has already been assigned or progressed.",
        status: "warning",
      });
      return;
    }

    try {
      setAssigningDriverId(driver.id);
      const config = await getAuthenticatedConfig();

      const refresh = await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${selectedJob._id}/refresh-candidates`,
        { radiusKm, expandRadius: false },
        config,
      );
      const refreshedJob = refresh.data?.data || selectedJob;
      const candidateIds = new Set(
        Array.isArray(refreshedJob?.candidateDriverIds)
          ? refreshedJob.candidateDriverIds.map((value: unknown) =>
              clean(value),
            )
          : [],
      );

      if (!candidateIds.has(clean(driver.id))) {
        setSelectedJob(refreshedJob);
        toast({
          title: "Driver is nearby but not eligible for this job",
          description:
            "The driver may not belong to the required warehouse/service pool or may have become unavailable.",
          status: "warning",
          duration: 4500,
          isClosable: true,
        });
        return;
      }

      const response = await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${selectedJob._id}/claim`,
        { driverId: driver.id },
        config,
      );
      const result = response.data?.data;

      if (result?.assigned === false) {
        toast({
          title: "Job already taken",
          description: "Another driver was assigned first.",
          status: "warning",
        });
      } else {
        toast({
          title: "Driver assigned",
          description: `${driver.name || "Driver"} is now assigned to this dispatch.`,
          status: "success",
        });
      }

      await loadJobs();
      await selectJob(selectedJob._id);
    } catch (error: any) {
      toast({
        title: "Could not assign driver",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "The driver could not be assigned.",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setAssigningDriverId("");
    }
  };

  const openDriver = async (driver: any) => {
    setSelectedDriver(driver);
    setSelectedDriverAddress(clean(driver?.lastKnownLocation?.text));
    const point = driver?.lastKnownLocation;
    if (!point) return;

    mapRef.current?.panTo({ lat: Number(point.lat), lng: Number(point.lng) });

    if (!clean(driver?.lastKnownLocation?.text)) {
      const address = await reverseGeocodeLatLng({
        lat: Number(point.lat),
        lng: Number(point.lng),
      });
      setSelectedDriverAddress(address);
    }
  };

  const mapBoundsDrivers = useMemo(
    () =>
      drivers.filter(
        (driver) =>
          Number.isFinite(Number(driver?.lastKnownLocation?.lat)) &&
          Number.isFinite(Number(driver?.lastKnownLocation?.lng)),
      ),
    [drivers],
  );

  useEffect(() => {
    if (
      !fitMapRequestedRef.current ||
      !mapRef.current ||
      !isLoaded ||
      !mapBoundsDrivers.length
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(center);
    mapBoundsDrivers.forEach((driver) =>
      bounds.extend({
        lat: Number(driver.lastKnownLocation.lat),
        lng: Number(driver.lastKnownLocation.lng),
      }),
    );
    mapRef.current.fitBounds(bounds, 48);
    fitMapRequestedRef.current = false;
  }, [center, isLoaded, mapBoundsDrivers]);

  if (accessLoading) {
    return (
      <Flex minH="320px" align="center" justify="center" gap={3}>
        <Spinner />
        <Text color={theme.textMuted}>Loading driver access…</Text>
      </Flex>
    );
  }

  if (!userId) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="md" color={theme.text}>
          Sign in required
        </Heading>
        <Text mt={2} color={theme.textMuted}>
          Sign in before opening the live driver map.
        </Text>
      </Box>
    );
  }

  if (loadError) {
    return <Box color="red.500">Google Maps failed to load.</Box>;
  }

  if (!isLoaded) {
    return (
      <Flex minH="320px" align="center" justify="center" gap={3}>
        <Spinner />
        <Text color={theme.textMuted}>Loading live driver map…</Text>
      </Flex>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
          <Box>
            <Heading size="md" color={theme.text}>
              Live Driver Map
            </Heading>
            <Text mt={1} color={theme.textMuted} fontSize="sm">
              Hybrid dispatch: every eligible driver attached to the selected
              warehouse remains a candidate, while live GPS adds nearby drivers
              from the same account and ranks them by pickup distance.
            </Text>
          </Box>
          <HStack flexWrap="wrap">
            {selectedJob ? (
              <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                {warehouseDriverCount} warehouse
              </Badge>
            ) : null}
            <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
              {nearbyDriverCount} nearby
            </Badge>
            <Badge borderRadius="full" px={3} py={1}>
              {drivers.length} shown
            </Badge>
            <Badge
              colorScheme={autoRefreshEnabled ? "cyan" : "gray"}
              borderRadius="full"
              px={3}
              py={1}
            >
              {autoRefreshEnabled ? "Live • 10s" : "Auto refresh off"}
            </Badge>
            {lastUpdatedAt ? (
              <Text fontSize="xs" color={theme.textMuted}>
                Updated {lastUpdatedAt.toLocaleTimeString()}
              </Text>
            ) : null}
            <Button
              size="sm"
              variant={autoRefreshEnabled ? "solid" : "outline"}
              onClick={() => setAutoRefreshEnabled((value) => !value)}
              borderRadius="10px"
            >
              {autoRefreshEnabled ? "Pause live" : "Resume live"}
            </Button>
            <Button
              size="sm"
              leftIcon={<RefreshCw size={15} />}
              onClick={() => void refreshCurrentMapData()}
              isLoading={driversLoading}
              borderRadius="10px"
            >
              Refresh
            </Button>
          </HStack>
        </HStack>
      </Box>

      <Box
        p={{ base: 3, md: 4 }}
        border="1px solid"
        borderColor={theme.border}
        borderRadius="12px"
        bg={theme.surface}
      >
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm" color={theme.text}>
              Dispatch job / order
            </FormLabel>
            <Select
              value={selectedJobId}
              onChange={(event) => void selectJob(event.target.value)}
              placeholder={
                jobsLoading ? "Loading jobs…" : "Browse any location"
              }
              borderRadius="10px"
            >
              {scopedJobs.map((job) => (
                <option key={job._id} value={job._id}>
                  {getJobLabel(job)}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" color={theme.text}>
              Search map location
            </FormLabel>
            <AutocompleteInput
              value={
                selectedJob ? selectedJob.pickupAddress || "" : centerAddress
              }
              placeholder="Search pickup area"
              onPlaceSelected={handlePlaceSelected}
              onLocationSelected={handleLocationSelected}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" color={theme.text}>
              Radius
            </FormLabel>
            <Select
              value={radiusKm}
              onChange={(event) => {
                const next = Number(event.target.value);
                setRadiusKm(next);
                void refreshCurrentMapData({ radius: next });
              }}
              borderRadius="10px"
            >
              {RADIUS_OPTIONS.map((radius) => (
                <option key={radius} value={radius}>
                  {radius} km
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" color={theme.text}>
              Service
            </FormLabel>
            <Select
              value={serviceCategory}
              onChange={(event) => setServiceCategory(event.target.value)}
              borderRadius="10px"
              isDisabled={Boolean(selectedJob)}
            >
              <option value="delivery">Delivery</option>
              <option value="product_order">Product orders</option>
              <option value="accommodation">Accommodation</option>
              <option value="car_hire">Car hire</option>
              <option value="trip">Trips / hike</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        <Flex mt={3} gap={2} wrap="wrap" align="center">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Crosshair size={15} />}
            onClick={useMyLocation}
            borderRadius="10px"
          >
            Use my location
          </Button>
          <Button
            size="sm"
            variant={onlyAvailable ? "solid" : "outline"}
            onClick={() => setOnlyAvailable((value) => !value)}
            borderRadius="10px"
          >
            {onlyAvailable ? "Available only" : "Include busy/off-shift"}
          </Button>
          <Button
            size="sm"
            variant={includeStale ? "solid" : "outline"}
            onClick={() => setIncludeStale((value) => !value)}
            borderRadius="10px"
          >
            {includeStale ? "Including stale GPS" : "Fresh GPS only"}
          </Button>
          <HStack flex="1" minW={{ base: "100%", md: "320px" }}>
            <Search size={15} />
            <Input
              size="sm"
              value={driverSearch}
              onChange={(event) => setDriverSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void refreshCurrentMapData();
              }}
              placeholder="Driver name, phone, vehicle or ID"
              borderRadius="10px"
            />
            <Button
              size="sm"
              onClick={() => void refreshCurrentMapData()}
              isLoading={driversLoading}
              borderRadius="10px"
            >
              Search
            </Button>
          </HStack>
        </Flex>
      </Box>

      {driversError ? (
        <Box p={3} borderRadius="10px" bg="red.50" color="red.700">
          {driversError}
        </Box>
      ) : null}

      <Flex direction={{ base: "column", xl: "row" }} gap={4} align="stretch">
        <Box
          flex="1 1 65%"
          minH={{ base: "420px", lg: "590px" }}
          border="1px solid"
          borderColor={theme.border}
          borderRadius="12px"
          overflow="hidden"
          position="relative"
        >
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%",
              minHeight: "590px",
            }}
            center={center}
            zoom={12}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              clickableIcons: false,
            }}
          >
            <Circle
              center={center}
              radius={radiusKm * 1000}
              options={{
                strokeColor: "#2563eb",
                strokeOpacity: 0.55,
                strokeWeight: 1.5,
                fillColor: "#2563eb",
                fillOpacity: 0.07,
              }}
            />

            <Marker
              position={center}
              title={selectedJob ? "Order pickup" : "Search center"}
              label={{ text: "P", color: "white", fontWeight: "700" }}
            />

            {mapBoundsDrivers.map((driver) => {
              const position = {
                lat: Number(driver.lastKnownLocation.lat),
                lng: Number(driver.lastKnownLocation.lng),
              };
              return (
                <Marker
                  key={driver.id}
                  position={position}
                  icon={createVehicleMarkerIcon(
                    driver.vehicleType,
                    driver.lastKnownLocation?.heading,
                  )}
                  title={`${driver.name || "Driver"}${
                    driver.distanceKm != null
                      ? ` • ${Number(driver.distanceKm).toFixed(1)} km`
                      : ""
                  }`}
                  onClick={() => void openDriver(driver)}
                  zIndex={selectedDriver?.id === driver.id ? 999 : 10}
                />
              );
            })}

            {selectedDriver?.lastKnownLocation ? (
              <InfoWindow
                position={{
                  lat: Number(selectedDriver.lastKnownLocation.lat),
                  lng: Number(selectedDriver.lastKnownLocation.lng),
                }}
                onCloseClick={() => setSelectedDriver(null)}
              >
                <Box maxW="260px" color="gray.800">
                  <Text fontWeight="800">
                    {selectedDriver.name || "Driver"}
                  </Text>
                  <Text fontSize="sm">
                    {vehicleLabel(selectedDriver.vehicleType)}
                    {selectedDriver.distanceKm != null
                      ? ` • ${Number(selectedDriver.distanceKm).toFixed(1)} km away`
                      : " • GPS distance unavailable"}
                  </Text>
                  <Text fontSize="xs" mt={1} color="gray.600">
                    {selectedDriverAddress || "Resolving location…"}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    GPS {formatLocationAge(selectedDriver.locationAgeSeconds)}
                  </Text>
                </Box>
              </InfoWindow>
            ) : null}
          </GoogleMap>

          {driversLoading ? (
            <Flex
              position="absolute"
              top={3}
              right={3}
              px={3}
              py={2}
              bg="whiteAlpha.900"
              borderRadius="10px"
              align="center"
              gap={2}
              boxShadow="sm"
            >
              <Spinner size="sm" />
              <Text fontSize="xs" color="gray.700">
                Refreshing drivers…
              </Text>
            </Flex>
          ) : null}
        </Box>

        <Box
          flex="1 1 35%"
          minW={{ xl: "350px" }}
          maxH={{ xl: "590px" }}
          overflowY="auto"
          border="1px solid"
          borderColor={theme.border}
          borderRadius="12px"
          bg={theme.surface}
          p={3}
        >
          <HStack justify="space-between" mb={3}>
            <Box>
              <Text fontWeight="800" color={theme.text}>
                Nearest drivers
              </Text>
              <Text fontSize="xs" color={theme.textMuted}>
                Warehouse drivers are always retained; nearby GPS drivers are
                added and live distance is used for ranking.
              </Text>
            </Box>
            <MapPin size={18} />
          </HStack>

          {!driversLoading && drivers.length === 0 ? (
            <Box p={5} textAlign="center">
              <Truck size={30} style={{ margin: "0 auto 8px" }} />
              <Text fontWeight="700" color={theme.text}>
                No live drivers here
              </Text>
              <Text mt={1} fontSize="sm" color={theme.textMuted}>
                Increase the radius or check driver shift/location sharing.
              </Text>
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {drivers.map((driver, index) => {
                const candidate =
                  !selectedJob || selectedJobCandidateIds.has(clean(driver.id));
                const selected = selectedDriver?.id === driver.id;

                return (
                  <Box
                    key={driver.id}
                    p={3}
                    border="1px solid"
                    borderColor={selected ? theme.primary : theme.border}
                    bg={selected ? theme.surfaceAlt : theme.surface}
                    borderRadius="10px"
                    cursor="pointer"
                    onClick={() => void openDriver(driver)}
                    _hover={{ borderColor: theme.primary }}
                  >
                    <Flex justify="space-between" gap={3} align="start">
                      <HStack align="start" spacing={3}>
                        <Flex
                          minW="34px"
                          h="34px"
                          borderRadius="9px"
                          align="center"
                          justify="center"
                          bg={theme.surfaceAlt}
                          fontWeight="800"
                          color={theme.text}
                        >
                          {index + 1}
                        </Flex>
                        <Box>
                          <Text fontWeight="800" color={theme.text}>
                            {driver.name || driver.id}
                          </Text>
                          <Text fontSize="xs" color={theme.textMuted}>
                            {vehicleLabel(driver.vehicleType)}
                            {driver.distanceKm != null
                              ? ` • ${Number(driver.distanceKm).toFixed(1)} km • ~${driver.estimatedPickupMinutes || "-"} min`
                              : " • GPS location not available"}
                          </Text>
                          <Text fontSize="xs" color={theme.textMuted} mt={1}>
                            GPS {formatLocationAge(driver.locationAgeSeconds)} •{" "}
                            {Number(driver.currentJobsCount || 0)}/
                            {Number(driver.maxConcurrentJobs || 2)} jobs • ⭐{" "}
                            {Number(driver.ratingScore || 0).toFixed(1)}
                          </Text>
                        </Box>
                      </HStack>
                      <VStack align="end" spacing={1}>
                        <Badge
                          colorScheme={
                            driver.candidateSource === "warehouse_nearby"
                              ? "purple"
                              : driver.candidateSource === "warehouse"
                                ? "blue"
                                : "green"
                          }
                        >
                          {driver.candidateSource === "warehouse_nearby"
                            ? "Warehouse + Nearby"
                            : driver.candidateSource === "warehouse"
                              ? "Warehouse"
                              : "Nearby"}
                        </Badge>
                        <Badge
                          colorScheme={
                            !driver.lastKnownLocation
                              ? "gray"
                              : Number(driver.locationAgeSeconds || 0) <= 60
                                ? "green"
                                : driver.locationFresh
                                  ? "yellow"
                                  : "orange"
                          }
                        >
                          {!driver.lastKnownLocation
                            ? "No GPS"
                            : Number(driver.locationAgeSeconds || 0) <= 60
                              ? "Live"
                              : driver.locationFresh
                                ? "Recent"
                                : "Stale"}
                        </Badge>
                      </VStack>
                    </Flex>

                    {selectedJob ? (
                      <Button
                        mt={3}
                        w="100%"
                        size="sm"
                        colorScheme={candidate ? "blue" : "gray"}
                        variant={candidate ? "solid" : "outline"}
                        isDisabled={
                          !candidate ||
                          !isClaimable(selectedJob.status) ||
                          Boolean(selectedJob.assignedDriverId)
                        }
                        isLoading={assigningDriverId === driver.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          void assignDriver(driver);
                        }}
                        borderRadius="9px"
                      >
                        {candidate
                          ? "Assign this driver"
                          : "Not eligible for this job"}
                      </Button>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Flex>
    </VStack>
  );
};

const DriverLiveMapPage: React.FC = () => (
  <AdminAccessProvider>
    <DriverLiveMapContent />
  </AdminAccessProvider>
);

export default DriverLiveMapPage;
